import { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Pencil, Trash2, Package, X, ImagePlus, ShoppingCart, Clock } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatDeliveryTime = (hours) => {
  if (hours == null) return '-';
  if (hours < 24) return `${hours} hr${hours !== 1 ? 's' : ''}`;
  const days = Math.floor(hours / 24);
  const rem = hours % 24;
  if (rem === 0) return `${days} day${days !== 1 ? 's' : ''}`;
  return `${days}d ${rem}h`;
};

const VendorProducts = () => {
  const [items, setItems] = useState([]);
  const [pickupPoints, setPickupPoints] = useState([]);
  const [categories, setCategories] = useState([]);
  const [allergens, setAllergens] = useState([]);
  const [heatProfiles, setHeatProfiles] = useState([]);
  const [dietaryRestrictions, setDietaryRestrictions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category_id: '',
    images: [],
    price: '',
    delivery_value: '',
    delivery_unit: 'hours',
    ingredients: '',
    allergen_ids: [],
    heat_profile_id: '',
    dietary_restriction_id: '',
    pickup_point_ids: [],
    home_delivery_allowed: true,
  });
  const [newImageUrl, setNewImageUrl] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchItems();
    fetchPickupPoints();
    fetchCategories();
    fetchAllergens();
    fetchHeatProfiles();
    fetchDietaryRestrictions();
  }, []);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/vendor/products`, { withCredentials: true });
      setItems(response.data);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPickupPoints = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/vendor/pickup-points`, { withCredentials: true });
      setPickupPoints(response.data);
    } catch (error) {
      console.error('Failed to fetch pickup points:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/vendor/categories`, { withCredentials: true });
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchAllergens = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/vendor/allergens`, { withCredentials: true });
      setAllergens(response.data);
    } catch (error) {
      console.error('Failed to fetch allergens:', error);
    }
  };

  const fetchHeatProfiles = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/vendor/heat-profiles`, { withCredentials: true });
      setHeatProfiles(response.data);
    } catch (error) {
      console.error('Failed to fetch heat profiles:', error);
    }
  };

  const fetchDietaryRestrictions = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/vendor/dietary-restrictions`, { withCredentials: true });
      setDietaryRestrictions(response.data);
    } catch (error) {
      console.error('Failed to fetch dietary restrictions:', error);
    }
  };

  const hoursToForm = (totalHours) => {
    if (totalHours == null || totalHours === '') return { value: '', unit: 'hours' };
    if (totalHours >= 24 && totalHours % 24 === 0) return { value: String(totalHours / 24), unit: 'days' };
    return { value: String(totalHours), unit: 'hours' };
  };

  const formToHours = () => {
    const val = parseFloat(formData.delivery_value);
    if (isNaN(val) || val <= 0) return null;
    return formData.delivery_unit === 'days' ? Math.round(val * 24) : Math.round(val);
  };

  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditingItem(item);
      const dt = hoursToForm(item.delivery_time_hours);
      setFormData({
        name: item.name || '',
        description: item.description || '',
        category_id: item.category_id || '',
        images: item.images || [],
        price: item.price ?? '',
        delivery_value: dt.value,
        delivery_unit: dt.unit,
        ingredients: item.ingredients || '',
        allergen_ids: item.allergen_ids || [],
        heat_profile_id: item.heat_profile_id || '',
        dietary_restriction_id: item.dietary_restriction_id || '',
        pickup_point_ids: item.pickup_point_ids || [],
        home_delivery_allowed: item.home_delivery_allowed !== false,
      });
    } else {
      setEditingItem(null);
      setFormData({ name: '', description: '', category_id: '', images: [], price: '', delivery_value: '', delivery_unit: 'hours', ingredients: '', allergen_ids: [], heat_profile_id: '', dietary_restriction_id: '', pickup_point_ids: pickupPoints.map((pp) => pp.id), home_delivery_allowed: true });
    }
    setNewImageUrl('');
    setDialogOpen(true);
  };

  const handleAddImage = () => {
    const url = newImageUrl.trim();
    if (url && !formData.images.includes(url)) {
      setFormData({ ...formData, images: [...formData.images, url] });
      setNewImageUrl('');
    }
  };

  const handleRemoveImage = (url) => {
    setFormData({ ...formData, images: formData.images.filter((u) => u !== url) });
  };

  const togglePickupPoint = (id) => {
    const ids = formData.pickup_point_ids.includes(id)
      ? formData.pickup_point_ids.filter((pid) => pid !== id)
      : [...formData.pickup_point_ids, id];
    setFormData({ ...formData, pickup_point_ids: ids });
  };

  const toggleAllergen = (id) => {
    const ids = formData.allergen_ids.includes(id)
      ? formData.allergen_ids.filter((aid) => aid !== id)
      : [...formData.allergen_ids, id];
    setFormData({ ...formData, allergen_ids: ids });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        name: formData.name,
        description: formData.description || null,
        category_id: formData.category_id || null,
        images: formData.images,
        price: formData.price !== '' ? parseFloat(formData.price) : null,
        delivery_time_hours: formToHours(),
        ingredients: formData.ingredients || null,
        allergen_ids: formData.allergen_ids,
        heat_profile_id: formData.heat_profile_id || null,
        dietary_restriction_id: formData.dietary_restriction_id || null,
        pickup_point_ids: formData.pickup_point_ids,
        home_delivery_allowed: formData.home_delivery_allowed,
      };
      if (editingItem) {
        await axios.put(`${API_URL}/api/vendor/products/${editingItem.id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/api/vendor/products`, payload, { withCredentials: true });
      }
      setDialogOpen(false);
      fetchItems();
    } catch (error) {
      console.error('Failed to save:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      await axios.delete(`${API_URL}/api/vendor/products/${id}`, { withCredentials: true });
      fetchItems();
    } catch (error) {
      console.error('Failed to delete:', error);
    }
  };

  const handleToggleStatus = async (item) => {
    try {
      const res = await axios.patch(`${API_URL}/api/vendor/products/${item.id}/toggle`, {}, { withCredentials: true });
      setItems(prev => prev.map(p => p.id === item.id ? { ...p, is_active: res.data.is_active } : p));
    } catch (error) {
      console.error('Failed to toggle status:', error);
    }
  };

  return (
    <div data-testid="vendor-products-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold text-text-primary mb-1">Products</h1>
          <p className="font-body text-text-secondary">Manage your product listings</p>
        </div>
        <Button
          onClick={() => handleOpenDialog()}
          className="bg-brand-primary hover:bg-brand-primary-hover text-white rounded-xl gap-2"
          data-testid="add-product-btn"
        >
          <Plus size={18} />
          Add Product
        </Button>
      </div>

      {/* Product Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-2xl border border-surface-border flex flex-col items-center justify-center h-48 text-text-muted">
          <Package size={48} className="mb-4 opacity-40" />
          <p className="font-body">No products yet</p>
          <Button variant="link" onClick={() => handleOpenDialog()} className="text-brand-primary">
            Add your first product
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {items.map((item) => (
            <div key={item.id} className={`bg-white rounded-2xl border border-surface-border overflow-hidden flex flex-col group hover:shadow-md transition-shadow ${!item.is_active ? 'opacity-60' : ''}`} data-testid={`product-card-${item.id}`}>
              {/* Image */}
              <div className="relative h-40 bg-background-secondary">
                {item.images && item.images.length > 0 ? (
                  <img
                    src={item.images[0]}
                    alt={item.name}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Package size={36} className="text-text-muted opacity-30" />
                  </div>
                )}
                {/* Actions overlay */}
                <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => handleOpenDialog(item)}
                    className="p-1.5 bg-white/90 backdrop-blur rounded-lg text-text-secondary hover:text-brand-primary shadow-sm"
                    title="Edit"
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    onClick={() => handleDelete(item.id)}
                    className="p-1.5 bg-white/90 backdrop-blur rounded-lg text-text-secondary hover:text-red-600 shadow-sm"
                    title="Delete"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
                {/* Category badge */}
                {item.category_name && (
                  <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-xs font-medium bg-white/90 backdrop-blur text-amber-700 shadow-sm">
                    {item.category_name}
                  </span>
                )}
              </div>

              {/* Info */}
              <div className="p-4 flex-1 flex flex-col">
                <h3 className="font-body font-semibold text-text-primary mb-1 line-clamp-1">{item.name}</h3>
                {item.description && (
                  <p className="font-body text-sm text-text-muted line-clamp-2 mb-3">{item.description}</p>
                )}
                <div className="mt-auto flex items-center justify-between">
                  <span className="font-heading font-bold text-lg text-text-primary">
                    {item.price != null ? `₹${item.price}` : '-'}
                  </span>
                  {item.delivery_time_hours != null && (
                    <span className="flex items-center gap-1 text-xs text-text-muted font-body">
                      <Clock size={12} />
                      {formatDeliveryTime(item.delivery_time_hours)}
                    </span>
                  )}
                </div>
              </div>

              {/* Status toggle */}
              <div className="mx-4 mb-3 flex items-center justify-between rounded-xl px-3 py-2" style={{ backgroundColor: item.is_active !== false ? '#ecfdf5' : '#f9fafb' }}>
                <span className={`font-body text-sm font-medium ${item.is_active !== false ? 'text-green-700' : 'text-gray-500'}`}>
                  {item.is_active !== false ? 'Active' : 'Inactive'}
                </span>
                <Switch
                  checked={item.is_active !== false}
                  onCheckedChange={() => handleToggleStatus(item)}
                  data-testid={`product-toggle-${item.id}`}
                />
              </div>

              {/* Order count highlight */}
              <div className="border-t border-surface-border bg-brand-primary/5 px-4 py-2.5 flex items-center gap-2">
                <ShoppingCart size={14} className="text-brand-primary" />
                <span className="font-body text-sm font-semibold text-brand-primary">
                  {item.order_count || 0} order{(item.order_count || 0) !== 1 ? 's' : ''}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingItem ? 'Edit' : 'Add'} Product
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="prod-name" className="font-body font-medium">
                Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="prod-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Product name"
                className="rounded-xl"
                data-testid="product-name-input"
              />
            </div>

            {/* Category */}
            <div className="space-y-2">
              <Label className="font-body font-medium">Category</Label>
              <Select
                value={formData.category_id}
                onValueChange={(value) => setFormData({ ...formData, category_id: value })}
              >
                <SelectTrigger className="rounded-xl" data-testid="product-category-select">
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {categories.length === 0 && (
                <p className="text-xs text-amber-600">No categories found. Ask admin to add categories.</p>
              )}
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="prod-desc" className="font-body font-medium">Description</Label>
              <textarea
                id="prod-desc"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe your product"
                rows={3}
                className="flex w-full rounded-xl border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring font-body"
                data-testid="product-desc-input"
              />
            </div>

            {/* Images */}
            <div className="space-y-2">
              <Label className="font-body font-medium">Images (by URL)</Label>
              <div className="flex gap-2">
                <Input
                  value={newImageUrl}
                  onChange={(e) => setNewImageUrl(e.target.value)}
                  placeholder="Paste image URL"
                  className="rounded-xl flex-1"
                  data-testid="product-image-url-input"
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddImage(); } }}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleAddImage}
                  disabled={!newImageUrl.trim()}
                  className="rounded-xl gap-1 shrink-0"
                  data-testid="product-add-image-btn"
                >
                  <ImagePlus size={16} />
                  Add
                </Button>
              </div>
              {formData.images.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.images.map((url, i) => (
                    <div key={i} className="relative group">
                      <img
                        src={url}
                        alt={`img-${i}`}
                        className="w-16 h-16 rounded-lg object-cover border border-surface-border"
                        onError={(e) => { e.target.src = ''; e.target.className = 'w-16 h-16 rounded-lg bg-red-50 border border-red-200'; }}
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveImage(url)}
                        className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Price + Delivery Time */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="prod-price" className="font-body font-medium">Price</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted font-medium">&#8377;</span>
                  <Input
                    id="prod-price"
                    type="number"
                    step="any"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    placeholder="0.00"
                    className="rounded-xl pl-8"
                    data-testid="product-price-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="font-body font-medium">Delivery Time</Label>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    min="0"
                    value={formData.delivery_value}
                    onChange={(e) => setFormData({ ...formData, delivery_value: e.target.value })}
                    placeholder="0"
                    className="rounded-xl flex-1"
                    data-testid="product-delivery-value-input"
                  />
                  <Select
                    value={formData.delivery_unit}
                    onValueChange={(value) => setFormData({ ...formData, delivery_unit: value })}
                  >
                    <SelectTrigger className="rounded-xl w-[100px]" data-testid="product-delivery-unit-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="hours">Hours</SelectItem>
                      <SelectItem value="days">Days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <p className="text-xs text-text-muted">Stored as hours internally for overdue tracking</p>
              </div>
            </div>

            {/* Ingredients */}
            <div className="space-y-2">
              <Label htmlFor="prod-ingredients" className="font-body font-medium">Ingredients</Label>
              <textarea
                id="prod-ingredients"
                value={formData.ingredients}
                onChange={(e) => setFormData({ ...formData, ingredients: e.target.value })}
                placeholder="List the ingredients used"
                rows={2}
                className="flex w-full rounded-xl border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring font-body"
                data-testid="product-ingredients-input"
              />
            </div>

            {/* Allergens - Multiselect */}
            <div className="space-y-2">
              <Label className="font-body font-medium">Allergens</Label>
              {allergens.length === 0 ? (
                <p className="text-xs text-amber-600">No allergens found. Ask admin to add allergens.</p>
              ) : (
                <div className="border border-surface-border rounded-xl p-3 space-y-2 max-h-36 overflow-y-auto">
                  {allergens.map((a) => (
                    <label key={a.id} className="flex items-center gap-2 cursor-pointer hover:bg-background-secondary p-1.5 rounded-lg transition-colors">
                      <input
                        type="checkbox"
                        checked={formData.allergen_ids.includes(a.id)}
                        onChange={() => toggleAllergen(a.id)}
                        className="rounded border-surface-border"
                        data-testid={`allergen-check-${a.id}`}
                      />
                      <span className="font-body text-sm">{a.name}</span>
                    </label>
                  ))}
                </div>
              )}
              {formData.allergen_ids.length > 0 && (
                <p className="text-xs text-text-muted">{formData.allergen_ids.length} allergen(s) selected</p>
              )}
            </div>

            {/* Heat Profile - Single Select */}
            <div className="space-y-2">
              <Label className="font-body font-medium">Heat Profile</Label>
              <Select
                value={formData.heat_profile_id}
                onValueChange={(value) => setFormData({ ...formData, heat_profile_id: value })}
              >
                <SelectTrigger className="rounded-xl" data-testid="product-heat-profile-select">
                  <SelectValue placeholder="Select heat profile" />
                </SelectTrigger>
                <SelectContent>
                  {heatProfiles.map((hp) => (
                    <SelectItem key={hp.id} value={hp.id}>{hp.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {heatProfiles.length === 0 && (
                <p className="text-xs text-amber-600">No heat profiles found. Ask admin to add heat profiles.</p>
              )}
            </div>

            {/* Dietary Restriction - Single Select */}
            <div className="space-y-2">
              <Label className="font-body font-medium">Dietary Restriction</Label>
              <Select
                value={formData.dietary_restriction_id}
                onValueChange={(value) => setFormData({ ...formData, dietary_restriction_id: value })}
              >
                <SelectTrigger className="rounded-xl" data-testid="product-dietary-restriction-select">
                  <SelectValue placeholder="Select dietary restriction" />
                </SelectTrigger>
                <SelectContent>
                  {dietaryRestrictions.map((dr) => (
                    <SelectItem key={dr.id} value={dr.id}>
                      <span className="flex items-center gap-2">
                        {dr.image_url && <img src={dr.image_url} alt="" className="w-4 h-4 rounded object-cover inline-block" />}
                        {dr.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {dietaryRestrictions.length === 0 && (
                <p className="text-xs text-amber-600">No dietary restrictions found. Ask admin to add them.</p>
              )}
            </div>

            {/* Allowed Pickup Points */}
            <div className="space-y-2">
              <Label className="font-body font-medium">Allowed Pickup Points</Label>
              {pickupPoints.length === 0 ? (
                <p className="text-xs text-amber-600">No pickup points found. Add pickup points first.</p>
              ) : (
                <div className="border border-surface-border rounded-xl p-3 space-y-2 max-h-40 overflow-y-auto">
                  {pickupPoints.map((pp) => (
                    <label
                      key={pp.id}
                      className="flex items-center gap-2 cursor-pointer hover:bg-background-secondary p-1.5 rounded-lg transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={formData.pickup_point_ids.includes(pp.id)}
                        onChange={() => togglePickupPoint(pp.id)}
                        className="rounded border-surface-border"
                        data-testid={`pickup-check-${pp.id}`}
                      />
                      <span className="font-body text-sm">{pp.name}</span>
                      {pp.address && <span className="font-body text-xs text-text-muted">- {pp.address}</span>}
                    </label>
                  ))}
                </div>
              )}
              {formData.pickup_point_ids.length > 0 && (
                <p className="text-xs text-text-muted">{formData.pickup_point_ids.length} pickup point(s) selected</p>
              )}
            </div>

            {/* Home Delivery Allowed */}
            <div className="space-y-2">
              <Label className="font-body font-medium">Delivery Options</Label>
              <label className="flex items-start gap-2.5 cursor-pointer hover:bg-background-secondary p-2.5 rounded-xl transition-colors border border-surface-border">
                <input
                  type="checkbox"
                  checked={formData.home_delivery_allowed}
                  onChange={(e) => setFormData({ ...formData, home_delivery_allowed: e.target.checked })}
                  className="mt-0.5 rounded border-surface-border"
                  data-testid="product-home-delivery-allowed-check"
                />
                <span>
                  <span className="font-body text-sm font-medium text-text-primary block">Home Delivery Allowed</span>
                  <span className="font-body text-xs text-text-muted">
                    Customers can choose to have this delivered to their home (apartment + room).
                    Uncheck if this product is pickup-only.
                  </span>
                </span>
              </label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-xl">
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving || !formData.name}
              className="bg-brand-primary hover:bg-brand-primary-hover rounded-xl"
              data-testid="product-save-btn"
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VendorProducts;
